import React, { useEffect, useMemo, useState } from 'react';
  import BundleInlinePicker from './BundleInlinePicker';
  import IsoDatePicker from './IsoDatePicker';
  import PhotoLightbox from './PhotoLightbox';
  import { isBlockedPS } from '../utils/holds';
  import { seasonDatesForRoom } from '../utils/acSeasons';
  import { msOpenDates } from '../utils/hours';
  import { msSlotsClient } from '../utils/msClient';
  import { listRoomPhotos } from '../api/reservationsApi';
  import { psSlotsLocal, cbSessionsLocal, acAvailabilityLocal, psHasKSimultaneous, acQuoteLocal, acNightsBetween } from '../utils/availabilityLocal';
  import RecurringPicker from './RecurringPicker';
  import WaitlistOptIn from './WaitlistOptIn';
  import PreorderProductsStep from './PreorderProductsStep';
  import PSInlinePicker from './PSInlinePicker';
  import CBInlinePicker from './CBInlinePicker';
  import MSInlinePicker from './MSInlinePicker';
  import ACInlinePicker from './ACInlinePicker';

  function amtLabel(value, type, currency = 'EUR') {
    if (value == null) return null;
    const t = String(type || '').toLowerCase();
    if (t === 'percentage' || t === 'percent') return `${Number(value)}% of booking`;
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(value)); }
    catch { return `€${Number(value).toFixed(2)}`; }
  }
  function policyForOffering(feesCtx, offering) {
    const map = feesCtx?.map || {};
    const policiesById = {};
    (feesCtx?.policies || []).forEach(p => { policiesById[p.id] = p; });
    const allId = feesCtx?.all_policy_id || null;
    const modeForKind = {
      ps_service: 'personal_services',
      cb_class: 'class_based',
      ms_table_type: 'modular_seating',
      ac_room_type: 'accommodation',
    };
    const mode = modeForKind[offering.kind] || offering.kind;
    const idForKey = offering.kind === 'ms_table_type'
      ? offering.ms_seating_config_id
      : offering.id;
    const pid = map[`${mode}|${idForKey}`] || allId;
    return pid ? policiesById[pid] : null;
  }

  function editRuleForOffering(editCtx, offering) {
    const map = {};
    (editCtx?.applies || []).forEach(a => { map[`${a.mode}|${a.item_id}`] = a.rule_id; });
    const rulesById = {};
    (editCtx?.rules || []).forEach(r => { rulesById[r.id] = r; });
    const modeForKind = {
      ps_service: 'personal_services',
      cb_class: 'class_based',
      ms_table_type: 'modular_seating',
      ac_room_type: 'accommodation',
    };
    const mode = modeForKind[offering.kind] || offering.kind;
    const idForKey = offering.kind === 'ms_table_type'
      ? offering.ms_seating_config_id
      : offering.id;
    const rid = map[`${mode}|${idForKey}`];
    return rid ? rulesById[rid] : null;
  }

  function PolicyBlurb({ policy, currency, cancellationRules = [] }) {
    if (!policy) return null;
    const preAmt = policy.full_prepayment_required ? 'Full prepayment' :
      (policy.prepayment_amount != null ? `Prepayment: ${amtLabel(policy.prepayment_amount, policy.prepayment_type, currency)}` : null);
    const when = Number(policy.charge_before_value) > 0
      ? `${policy.charge_before_value} ${String(policy.charge_before_unit || 'hours')} before start`
      : 'at booking';
    // Show no-show fee only if amount > 0
    const noshowAmt = policy.no_show_fee_amount != null ? Number(policy.no_show_fee_amount) : 0;
    const noshow = noshowAmt > 0
      ? `No-show fee: ${amtLabel(policy.no_show_fee_amount, policy.no_show_fee_type, currency)}${policy.no_show_timeout_value ? ` — applies ${policy.no_show_timeout_value} ${String(policy.no_show_timeout_unit || 'minutes')} after start` : ''}`
      : null;
    const freeCancel = (policy.free_cancellation_before_value != null)
      ? `Free cancellation until ${policy.free_cancellation_before_value} ${String(policy.free_cancellation_before_unit || 'hours')} before start`
      : null;

    // find cancellation rules applicable to this policy (server provides cancellation_rules list with fee_policy_id)
    const applicableCancel = Array.isArray(cancellationRules) && policy?.id
      ? cancellationRules.filter(r => String(r.fee_policy_id) === String(policy.id))
      : [];

    return (
      <div className="mt-3 text-[12px] text-gray-700 bg-white border rounded p-2">
        {preAmt && <div>{preAmt} {preAmt && '—'} {when}</div>}
        {freeCancel && <div>{freeCancel}</div>}
        {noshow && <div>{noshow}</div>}
        {applicableCancel && applicableCancel.length > 0 && (
          <div className="mt-2 text-[12px] text-gray-700">
            <div className="font-semibold">Cancellation rules</div>
            {applicableCancel.map((r, i) => (
              <div key={i} className="text-[12px] text-gray-600">
                { /* rule label or time window */ }
                {r.label || `${r.time_value || r.time_before || ''} ${r.time_unit || r.time_before_unit || ''}`}
                { /* fee: prefer 'amount' + 'amount_type' from server, fall back to older fields */ }
                {(r.amount != null) ? ` — Fee: ${amtLabel(Number(r.amount), r.amount_type || r.amountType, currency)}` : (r.fee_amount ? ` — Fee: ${amtLabel(r.fee_amount, r.fee_type, currency)}` : '')}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  export default function OfferingCard({
    offering,
    onPick,
    bookingRange,
    cbSessions = [],
    acSeasons = [],
    timezone,
    firstBookableTs,
    hoursCtx,
    live,
    msRules,
    psSchedule = [],
    employeesIndex = {},
    psEmpWork, psOffDays,
    acModifiers,
    psScheduleMode,
    feesCtx,
    editCtx,
    currency = 'EUR',
    preorder,
    setPreorder,
    offeringsIndex = {},
    winStart = '',
    winEnd = '',
    defaultDate = ''
  }) {
    const [showInfo, setShowInfo] = useState(false);
    const labelForBundleItem = (it) => {
      if (!it) return '';
      if (it.title) return it.title;
      try {
        const mode = it.mode;
        const itemId = String(it.item_id);
        // find offering in offeringsIndex by kind and id or ms_seating_config_id
        const found = Object.values(offeringsIndex || {}).find(o => {
          if (!o) return false;
          if (mode === 'personal_services' && o.kind === 'ps_service' && String(o.id) === itemId) return true;
          if (mode === 'class_based' && o.kind === 'cb_class' && String(o.id) === itemId) return true;
          if (mode === 'modular_seating' && o.kind === 'ms_table_type' && (String(o.id) === itemId || String(o.ms_seating_config_id) === itemId)) return true;
          if (mode === 'accommodation' && o.kind === 'ac_room_type' && String(o.id) === itemId) return true;
          return false;
        });
        if (found) {
          if (found.kind === 'ms_table_type') return found.ms_area_label || found.ms_area_type || found.title || `${it.mode}:${it.item_id}`;
          return found.title || `${it.mode}:${it.item_id}`;
        }
      } catch (e) {}
      return `${it.mode}:${it.item_id}`;
    };
    const [boxOpen, setBoxOpen] = useState(false);
    const [lightbox, setLightbox] = useState({ open: false, photos: [], start: 0, loading: false, error: null });

    // Precompute PSInlinePicker props so we can log them consistently when rendering
    const psInlinePickerProps = {
      offering,
      onPick,
      employees: offering._employees || [],
      live,
      bookingRange,
      hoursCtx,
      psSchedule,
      psEmpWork, 
      psOffDays,
      timezone,
      firstBookableTs,
      psScheduleMode,
      winStart,
      winEnd,
      defaultDate,
      caller: 'OfferingCard',
    };
    const hasDiscount = offering.discount && offering.discount.final < offering.price;
    const perks = offering.perks || [];
    const [thumbs, setThumbs] = useState([]);
    const [thumbsLoading, setThumbsLoading] = useState(false);
    // --- Photo handling (unchanged) ---
    useEffect(() => {
      let cancelled = false;
      async function loadThumbs() {
        if (offering.kind !== 'ac_room_type') return;
        if (!(Number(offering.photos_count || 0) > 1)) return;

        setThumbsLoading(true);
        try {
          const { photos } = await listRoomPhotos(offering.business_id, offering.id);
          const arr = photos?.length ? photos : (offering.photo_url ? [offering.photo_url] : []);
          if (!cancelled) setThumbs(arr);
        } catch {
          if (!cancelled) setThumbs(offering.photo_url ? [offering.photo_url] : []);
        } finally {
          if (!cancelled) setThumbsLoading(false);
        }
      }
      loadThumbs();
      return () => { cancelled = true; };
    }, [offering.kind, offering.id, offering.business_id, offering.photos_count, offering.photo_url]);

    // UPDATED: prefer already-fetched thumbs for rooms
    async function openPhotos(start = 0) {
      if (offering.kind === 'ac_room_type') {
        // if we have multiple photos (thumbs), open those
        if (thumbs?.length > 1) {
          setLightbox({ open: true, photos: thumbs, start, loading: false, error: null });
          return;
        }
        // single photo fallback
        if (offering.photo_url) {
          setLightbox({ open: true, photos: [offering.photo_url], start: 0, loading: false, error: null });
          return;
        }
        return;
      }

      // Non-rooms: one hero photo (if present)
      if (offering.photo_url) {
        setLightbox({ open: true, photos: [offering.photo_url], start: 0, loading: false, error: null });
      }
    }

    return (
      <div className="border rounded-xl p-3 hover:shadow-sm transition">
  {/* ─── MEDIA AREA (rendered once) ───────────────────────── */}
  {offering.kind === 'ac_room_type' && Number(offering.photos_count || 0) > 1 ? (
    // Rooms with multiple photos → thumbnails only
    <div className="mb-2">
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {(thumbs.length ? thumbs : (offering.photo_url ? [offering.photo_url] : [])).map((src, i) => (
          <button
            key={i}
            onClick={() => openPhotos(i)}
            className="shrink-0 rounded-lg overflow-hidden border border-gray-200 hover:border-gray-400"
            aria-label={`Open photo ${i + 1}`}
            title={`Open photo ${i + 1}`}
          >
            <img src={src} alt="" loading="lazy" className="h-14 w-20 object-cover" />
          </button>
        ))}
      </div>
    </div>
  ) : (
    // Non-rooms OR rooms with a single photo → one hero image
    offering.photo_url ? (
      <button
        type="button"
        className="relative aspect-video bg-gray-100 rounded-lg mb-2 overflow-hidden w-full block group"
        onClick={() => openPhotos(0)}
        aria-label="View photo"
      >
        <img
          src={offering.photo_url}
          alt={offering.title}
          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
        />
      </button>
    ) : null
  )}

        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm uppercase tracking-wide text-gray-500">{offering.kind_label}</div>
            <div className="font-semibold">{offering.title}</div>
            {offering.kind === 'bundle' && offering.items && offering.items.length > 0 && (
              <div className="text-xs text-gray-600 mt-1">
                <div className="mb-1">Includes:</div>
                <div className="flex flex-wrap gap-2">
                  {offering.items.map((it, idx) => (
                    <span
                      key={idx}
                      title={labelForBundleItem(it)}
                      className="text-[12px] bg-gray-100 text-gray-800 rounded-full px-3 py-1 inline-block"
                    >
                      {labelForBundleItem(it)}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {offering.duration_min && (
              <div className="text-xs text-gray-600 mt-1">Duration: {offering.duration_min} min</div>
            )}
            {offering.kind === 'ac_room_type' && Number(offering.max_guests) > 0 && (
              <div className="text-xs text-gray-600 mt-1">Sleeps up to {offering.max_guests}</div>
            )}
            {offering.kind === 'ms_table_type' && (offering.ms_area_label || offering.ms_area_type) && (
              <div className="text-xs text-gray-600 mt-1">Area: {offering.ms_area_label || offering.ms_area_type}</div>
            )}
            {!!perks.length && (
              <div className="mt-1 flex flex-wrap gap-1">
                {perks.map((p, i) => (
                  <span key={i} className="text-[11px] bg-emerald-50 text-emerald-700 rounded px-2 py-0.5">
                    {p.type === 'free_product' ? `Free: ${p.name}` : `${p.percent}% off ${p.name}`}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="text-right relative">
            <div className="text-sm">
              {hasDiscount && (
                <span className="line-through text-gray-400 mr-1">
                  {offering.price_fmt || `€${Number(offering.price).toFixed(2)}`}
                </span>
              )}
              <span className="font-semibold">
                {offering.discount?.final_fmt || offering.price_fmt || `€${Number(offering.discount?.final ?? offering.price).toFixed(2)}`}
              </span>
            </div>

            <button
              onClick={() => setBoxOpen(v => !v)}
              className="mt-2 text-xs text-blue-700 hover:underline">
              {offering.kind === 'ac_room_type' ? 'Select date' : 'Select time'}
            </button>

            {/* Info icon to show fee policy + edit rules */}
            <button
              type="button"
              aria-label="View booking and change rules"
              title="View booking and change rules"
              className="ml-2 inline-flex items-center justify-center align-middle text-gray-500 hover:text-gray-800"
              onClick={() => setShowInfo(v => !v)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0ZM9 7a1 1 0 1 0 2 0 1 1 0 0 0-2 0Zm1-3a6 6 0 1 0 0 12A6 6 0 0 0 10 4Zm-1 5a1 1 0 1 1 2 0v4a1 1 0 1 1-2 0V9Z" clipRule="evenodd" />
              </svg>
            </button>

            {showInfo && (
              <div className="absolute right-0 z-10 mt-2 w-80 bg-white border border-gray-200 shadow-lg rounded-lg p-3 text-left">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold">Booking info</div>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-700"
                    onClick={() => setShowInfo(false)}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
                {/* Fee policy summary */}
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">Payment & fees</div>
                  <PolicyBlurb policy={policyForOffering(feesCtx, offering)} currency={currency} cancellationRules={feesCtx?.cancellation_rules} />
                </div>
                {/* Edit rules summary */}
                <div className="mt-3">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Change & edit rules</div>
                  {(() => {
                    const r = editRuleForOffering(editCtx, offering);
                    if (!r) return <div className="text-xs text-gray-600 mt-1">No specific edit rules. Default business policy applies.</div>;
                    const parts = [];
                    if (r.max_date_shift_days != null) parts.push(`Date can shift up to ${r.max_date_shift_days} day(s)`);
                    if (r.edit_cutoff_value != null) parts.push(`Edits allowed until ${r.edit_cutoff_value} ${String(r.edit_cutoff_unit||'hours')} before start`);
                    if (r.edit_fee_amount != null) parts.push(`Edit fee: ${amtLabel(r.edit_fee_amount, r.edit_fee_type, currency)}`);
                    if (r.max_edits_per_reservation != null) parts.push(`Max edits per reservation: ${r.max_edits_per_reservation}`);
                    const toggles = [];
                    if (r.allow_party_size_changes) toggles.push('party size');
                    if (r.allow_switch_service) toggles.push('switch service');
                    if (r.allow_switch_class) toggles.push('switch class');
                    if (r.allow_change_room_type) toggles.push('change room type');
                    if (r.allow_change_seating_area) toggles.push('change seating area');
                    return (
                      <div className="mt-1 text-[12px] text-gray-700 bg-white border rounded p-2">
                        <div>{r.label || 'Edit rules'}</div>
                        {parts.length > 0 && <div className="mt-1">{parts.join(' • ')}</div>}
                        {toggles.length > 0 && (
                          <div className="mt-1 text-gray-600">Allowed changes: {toggles.join(', ')}</div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
          </div>

        {/* Inline booking box (per-kind) */}
        {boxOpen && (
          <div className="mt-3 rounded-lg border bg-gray-50 p-3">
            {offering.kind === 'ps_service' && (
              (() => {
                return <PSInlinePicker {...psInlinePickerProps} />;
              })()
            )}
            {offering.kind === 'cb_class' && (
              <CBInlinePicker
                offering={offering}
                onPick={onPick}
                bookingRange={bookingRange}
                cbSessions={cbSessions}
                timezone={timezone}
                firstBookableTs={firstBookableTs}
                live={live}
                employeesIndex={employeesIndex}
                winStart={winStart}
                winEnd={winEnd}
                defaultDate={defaultDate}
              />
            )}
            {offering.kind === 'ms_table_type' && (
              <MSInlinePicker
                offering={offering}
                onPick={onPick}
                bookingRange={bookingRange}
                hoursCtx={hoursCtx}
                msRules={msRules}          // contains tables, combinations, seating_configs now
                live={live}
                timezone={timezone}
                firstBookableTs={firstBookableTs}
                winStart={winStart}
                winEnd={winEnd}
                defaultDate={defaultDate}
              />
            )}
            {offering.kind === 'ms_table_type' && !!preorder?.allow && (
              <div className="mt-3">
                <PreorderProductsStep
                  products={preorder?.products || []}
                  selected={preorder?.picked || []}
                  onChange={(picked) => setPreorder?.(p => ({ ...p, picked }))}
                />
              </div>
            )}
            {offering.kind === 'ac_room_type' && (
              <ACInlinePicker
                offering={offering}
                onPick={onPick}
                bookingRange={bookingRange}
                acSeasons={acSeasons}
                acModifiers={acModifiers}
                live={live}
                winStart={winStart}
                winEnd={winEnd}
                defaultDate={defaultDate}
              />
            )}
            {offering.kind === 'bundle' && (
              <BundleInlinePicker
                offering={offering}
                bookingRange={bookingRange}
                onPick={onPick}
                debug={true}
                hoursCtx={hoursCtx}
                msRules={msRules}
                live={live}
                cbSessions={cbSessions}
                psSchedule={psSchedule}
                employeesIndex={employeesIndex}
                acSeasons={acSeasons}
                firstBookableISO={firstBookableTs}
                offeringsIndex={offeringsIndex}
                timezone={timezone}
                winStart={winStart}
                winEnd={winEnd}
                defaultDate={defaultDate}
                psEmpWork={psEmpWork}
              />
            )}
          </div>
        )}

        {lightbox.open && !lightbox.loading && !!lightbox.photos.length && (
          <PhotoLightbox
            photos={lightbox.photos}
            startIndex={lightbox.start}
            onClose={() => setLightbox({ open: false, photos: [], start: 0, loading: false, error: null })}
          />
        )}
        {lightbox.open && lightbox.loading && (
          <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
            <div className="text-white text-sm">Loading photos…</div>
          </div>
        )}
      </div>
    );
  }

